import {Box, Typography} from '@mui/material';
import ArrDataGridPro from '../../common/arr-data-grid-pro/ArrDataGridPro';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import {t} from 'i18next';
import {columns} from './IndexManagementData';
import './IndexManagementData.scss';
import ArrMenu, {CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import ArrModal from '../../common/arr-modal/ArrModal';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {UseIndexManagement} from './IndexManagementController';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {setViewIndexId} from '../../../redux/slice/createCustomIndexSlice';
import {useDispatch} from 'react-redux';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const IndexManagement = () => {
  const {
    filterModel,
    handleFilterModelChange,
    handleSortModelChange,
    sortModel,
    handleMoreIconClick,
    handleViewIndex,
    pageNumber,
    pageSize,
    setPageNumber,
    setPageSize,
    actionMenuRef,
    onMenuItemClick,
    isDeleteCustomIndex,
    setIsDeleteCustomIndex,
    rebuildModalOpen,
    setRebuildModalOpen,
    clickedMenuRow,
    showToast,
    setShowToast,
    toastMessage,
    toastStatus,
    handleDeleteIndex,
    handleRebuildIndex,
    actionMenuItem,
    indexManagementData,
    indexManagementLoading,
    fetchIdLoading,
    pinnedColumn,
  } = UseIndexManagement();

  const dispatch = useDispatch();
  const menuTriggerRef = useRef<HTMLElement | null>(null);
  const isActionMenuOpenRef = useRef(false);
  const wasModalOpenRef = useRef(false);
  const restoreFocusToMenuTrigger = useCallback(() => {
    window.requestAnimationFrame(() => {
      menuTriggerRef.current?.focus();
    });
  }, []);

  const focusFirstMenuItem = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const menu = document.getElementById('more-menu');
        const firstEnabledMenuItem = menu?.querySelector<HTMLElement>(
          [
            '[role="menuitem"]:not([aria-disabled="true"])',
            '[role="menuitemradio"]:not([aria-disabled="true"])',
            '[role="menuitemcheckbox"]:not([aria-disabled="true"])',
          ].join(',')
        );
        firstEnabledMenuItem?.focus();
      });
    });
  }, []);

  const handleAccessibleMoreIconClick = useCallback(
    (...args: Parameters<typeof handleMoreIconClick>) => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        menuTriggerRef.current = activeElement;
      }
      isActionMenuOpenRef.current = true;
      const result = handleMoreIconClick(...args);
      focusFirstMenuItem();
      return result;
    },
    [handleMoreIconClick, focusFirstMenuItem]
  );

  const handleAccessibleMenuItemClick = useCallback(
    (...args: Parameters<typeof onMenuItemClick>) => {
      isActionMenuOpenRef.current = false;
      const result = onMenuItemClick(...args);
      restoreFocusToMenuTrigger();
      return result;
    },
    [onMenuItemClick, restoreFocusToMenuTrigger]
  );

  const indexManagementColumns = useMemo(
    () =>
      columns(handleAccessibleMoreIconClick, handleViewIndex).map((column) => {
        return column;
      }),
    [handleAccessibleMoreIconClick, handleViewIndex]
  );

  const filteredActions = useMemo((): CustomMenuItem[] => {
    const status = clickedMenuRow?.status ?? '';
    const disabledMap: Record<string, boolean> = {
      view: false,
      rebuildIndex: !['COMPLETE', 'FAILED'].includes(status),
      deleteIndex: !['COMPLETE', 'FAILED'].includes(status),
    };
    dispatch(setViewIndexId(clickedMenuRow?.customIndexID ?? 0));
    const mapAndFilter = (items: CustomMenuItem[]): CustomMenuItem[] =>
      items?.map((it) => ({
        id: String(it.id),
        label: it.label,
        disabled: disabledMap[it.id] ?? false,
        variant:
          it.id === 'deleteIndex' && ['COMPLETE', 'FAILED'].includes(status)
            ? 'danger'
            : 'heading',
      }));
    const actionMenuData = !clickedMenuRow?.customIndexID
      ? actionMenuItem?.filter(
          (actionMenu) =>
            actionMenu.id != 'deleteIndex' && actionMenu.id != 'rebuildIndex'
        )
      : actionMenuItem;
    return mapAndFilter(actionMenuData);
  }, [clickedMenuRow]);

  useEffect(() => {
    const handleMenuKeyboardClose = (event: KeyboardEvent) => {
      if (!isActionMenuOpenRef.current || event.key !== 'Escape') return;
      event.preventDefault();
      isActionMenuOpenRef.current = false;
      restoreFocusToMenuTrigger();
    };
    document.addEventListener('keydown', handleMenuKeyboardClose);
    return () =>
      document.removeEventListener('keydown', handleMenuKeyboardClose);
  }, [restoreFocusToMenuTrigger]);

  useEffect(() => {
    const isModalOpen = rebuildModalOpen || isDeleteCustomIndex;
    if (!isModalOpen && wasModalOpenRef.current) {
      restoreFocusToMenuTrigger();
    }
    wasModalOpenRef.current = isModalOpen;
  }, [rebuildModalOpen, isDeleteCustomIndex, restoreFocusToMenuTrigger]);

  useEffect(() => {
    if (!isDeleteCustomIndex) return;

    const dataTestId = 'delete-custom-index-modal';
    const animationFrameId = window.requestAnimationFrame(() => {
      const title = document.getElementById(`${dataTestId}-title`);
      const description = document.getElementById(`${dataTestId}-description`);
      const deleteDescription2 = document.querySelector<HTMLElement>(
        '[data-testid="index-management-desc2"]'
      );
      title?.setAttribute('tabindex', '0');
      description?.setAttribute('tabindex', '0');
      deleteDescription2?.setAttribute('tabindex', '0');
      title?.focus();
    });
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isDeleteCustomIndex]);

  const NoRowsOverlay = () => {
    if (indexManagementData.totalRecords !== 0) return null;
    return (
      <Box className="no-grid-overlay">
        <NoDataIcon />
        <Typography
          className="typo-text headline3_size"
          data-testid="no-meta-result"
        >
          {t('ingestions.indexManagement.noQueryReturnDataMsg')}
        </Typography>
      </Box>
    );
  };
  const rebuildModalDescription = (
    <>
      <Typography data-testid={`index-management-description1`}>
        {t('indexManagement.customIndex.rebuildModal.description1')}
      </Typography>
      <Typography data-testid={`index-management-typography`}>
        &nbsp;
      </Typography>
      <Typography data-testid={`index-management-description2`}>
        {t('indexManagement.customIndex.rebuildModal.description2')}
      </Typography>
    </>
  );
  const hasGridData = (indexManagementData?.totalRecords ?? 0) > 0;
  return (
    <Box
      className="index-management-data-grid"
      data-testid="history-grid-wrapper"
    >
      <ArrDataGridPro
        rows={indexManagementData?.items ?? []}
        columns={indexManagementColumns}
        density="compact"
        disableVirtualization
        headerFilters
        showPerPageOptions
        pagination
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        noRowsOverlay={NoRowsOverlay}
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        onPageChange={setPageNumber}
        currentPage={pageNumber}
        primaryKey="customIndexID"
        disableColumnResize={false}
        rowCount={indexManagementData?.totalRecords}
        hideFooter={indexManagementData?.totalRecords === 0}
        sortModel={hasGridData ? sortModel : []}
        onSortModelChange={hasGridData ? handleSortModelChange : undefined}
        disableColumnSorting={!hasGridData}
        entityType={t('ingestions.indexManagement.entries')}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        filterMode="server"
        paginationMode="server"
        pinnedRows={pinnedColumn?.length > 0 ? {top: pinnedColumn} : undefined}
      />
      <ArrMenu
        id="more-menu"
        ref={actionMenuRef}
        items={filteredActions}
        onItemClick={handleAccessibleMenuItemClick}
        direction="bottom-left"
      />
      {rebuildModalOpen && (
        <ArrModal
          showModal={rebuildModalOpen}
          showClose={false}
          onClose={() => {
            setRebuildModalOpen(false);
          }}
          title={t('indexManagement.customIndex.readOnly.rebuildIndex')}
          ariaLabel={
            t('indexManagement.customIndex.readOnly.rebuildIndex') + 'modal'
          }
          description={rebuildModalDescription}
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setRebuildModalOpen(false);
              },
              variant: 'secondary',
              ariaLabel: t('common.cancel'),
              dataTestId: 'rebuild-modal-cancel',
              ariaDescribedBy: t(
                'indexManagement.customIndex.rebuildModal.cancelBtnHint'
              ),
            },
            {
              id: '2',
              label: t('indexManagement.customIndex.readOnly.rebuildIndex'),
              onClick: () => {
                if (clickedMenuRow && clickedMenuRow.customIndexID) {
                  handleRebuildIndex(clickedMenuRow.customIndexID);
                }
              },
              variant: 'primary',
              dataTestId: 'rebuild-modal-confirm',
              ariaLabel: t('indexManagement.customIndex.readOnly.rebuildIndex'),
              ariaDescribedBy: t(
                'indexManagement.customIndex.rebuildModal.rebuildBtnHint'
              ),
            },
          ]}
          dataTestId={'index-management-modal'}
          initialFocusTarget="title"
        />
      )}
      {isDeleteCustomIndex && (
        <ArrModal
          showModal={isDeleteCustomIndex}
          showClose={false}
          onClose={() => {
            setIsDeleteCustomIndex(false);
          }}
          className="delete-custom-index-modal"
          title={t('ingestions.indexManagement.deleteCustomIndex.title')}
          description={t('ingestions.indexManagement.deleteCustomIndex.desc1', {
            indexName: clickedMenuRow?.indexName,
          })}
          dataTestId="delete-custom-index-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setIsDeleteCustomIndex(false);
              },
              variant: 'secondary',
              dataTestId: 'dci-cancel-btn',
            },
            {
              id: '2',
              label: t(
                'ingestions.indexManagement.deleteCustomIndex.deleteIndex'
              ),
              onClick: () => {
                if (clickedMenuRow && clickedMenuRow.customIndexID) {
                  handleDeleteIndex(clickedMenuRow.customIndexID);
                }
              },
              variant: 'danger',
              dataTestId: 'dci-delete-btn',
            },
          ]}
        >
          <Box className="custom-modal-body">
            <Typography
              className="typo-text body2_size desc-text-color"
              data-testid={`index-management-desc2`}
            >
              {t('ingestions.indexManagement.deleteCustomIndex.desc2')}
            </Typography>
          </Box>
        </ArrModal>
      )}
      {(indexManagementLoading || fetchIdLoading) && (
        <ArrLoadingSpinner overlay isOpen />
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default IndexManagement;
